import { Anchor, Button, Group, Modal, NumberInput, Select, Stack, Text } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { EntitySelect } from '../../components/EntityField';
import { EmptyNote } from '../../components/RecordScreen';
import { useEntityIndex } from '../../hooks/useEntityIndex';
import { useIslandToday } from '../../hooks/useSeedLocation';
import { enumOptions } from '../../lib/enums';
import {
  currencySymbol,
  formatDate,
  formatMoney,
  formatQty,
  type NumberInputValue,
  toMinorUnits,
  typedNumber,
} from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { useStockItems } from '../inventory/stock';
import { ImportPriceListForm } from './ImportPriceListForm';
import { PriceHistoryModal, priceHistoryPath, usePriceHistory } from './PriceHistoryModal';

const CATALOG_ITEMS_PATH = '/admin/v1/suppliers/supplier-catalog-items';

interface CatalogRow {
  id: string;
  inventoryItemId: string;
  sku?: string | null;
  description?: string | null;
  packQty: number;
  packUnit: string;
  priceMinor: number;
  currency: string;
  effectiveAt: string;
}

const catalogPath = (supplierId: string) => `/admin/v1/suppliers/suppliers/${supplierId}/catalog`;

/**
 * What this supplier charges, by the item it is a price FOR.
 *
 * Two things were wrong with reading it off their own paperwork. The row was named by whatever they
 * call it on their invoice — a code like `CRV-750X12`, or nothing at all — when the row already
 * carries the stock item it prices, so a manager comparing two vendors for rum was reading two
 * strings that had nothing to do with each other or with the rum. And a list price is written per
 * case while the bar counts bottles, so the figure that matters when an order goes out is the one
 * per bottle, which nobody was doing in their head reliably.
 *
 * Prices could also only ever be read here. Recording them is what makes an order fill itself in
 * rather than being typed from memory at eight in the morning.
 */
export function SupplierCatalogSection({
  supplierId,
  supplierName,
  currency,
}: {
  supplierId: string;
  supplierName: string;
  currency: string;
}) {
  const queryClient = useQueryClient();
  const stock = useStockItems();
  const items = useEntityIndex('inventoryItem');
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showingHistoryFor, setShowingHistoryFor] = useState<{ id?: string; name?: string } | null>(null);

  const catalog = useQuery({
    queryKey: listKey(catalogPath(supplierId)),
    queryFn: () => rawApi<CatalogRow[]>('GET', catalogPath(supplierId)),
    enabled: Boolean(supplierId),
  });

  /*
   * Their whole history, read once for the section rather than per row. It is what tells a row
   * whether there is anything worth opening: "Price history" beside a price nobody has ever
   * changed is a button that leads to one line, and offering it on every row teaches the reader
   * to stop pressing it — including on the row where the vendor put the price up twenty per cent.
   */
  const history = usePriceHistory(supplierId, undefined, Boolean(supplierId));
  const pricesOnFile = new Map(
    (history.data ?? []).map((h) => [h.inventoryItemId, h.entries.length] as const),
  );
  const everChanged = [...pricesOnFile.values()].some((count) => count > 1);

  const refreshPrices = () => {
    void queryClient.invalidateQueries({ queryKey: listKey(catalogPath(supplierId)) });
    void queryClient.invalidateQueries({ queryKey: listKey(priceHistoryPath(supplierId)) });
  };

  const rows = [...(catalog.data ?? [])].sort((a, b) =>
    items.label(a.inventoryItemId).localeCompare(items.label(b.inventoryItemId)),
  );

  return (
    <>
      <Group justify="flex-end" mb="sm" gap="xs">
        {/* Only once there is a history to read. A vendor whose every price has been quoted once
            has nothing behind this button, and a button that opens a single row is noise. */}
        {everChanged && (
          <Button size="compact-sm" variant="subtle" onClick={() => setShowingHistoryFor({})}>
            What they used to charge
          </Button>
        )}
        {/* A distributor's list arrives as a sheet, not as forty separate quotes. */}
        <Button size="compact-sm" variant="light" onClick={() => setImporting(true)}>
          Record a whole list
        </Button>
        <Button size="compact-sm" variant="light" onClick={() => setAdding(true)}>
          Record a price
        </Button>
      </Group>

      {catalog.isLoading ? (
        <EmptyNote>Loading…</EmptyNote>
      ) : catalog.isError ? (
        // Never the empty state: "nothing recorded" over an outage would have somebody typing a
        // price list back in that is already on file.
        <EmptyNote>
          Their prices could not be loaded. Nothing has been lost — try again in a moment.
        </EmptyNote>
      ) : rows.length === 0 ? (
        <EmptyNote>
          No prices recorded yet. Record what they charge and an order to them fills its own prices in.
        </EmptyNote>
      ) : (
        <Stack gap={4}>
          {rows.map((line) => {
            const unit = stock.unitOf(line.inventoryItemId);
            const packed = line.packQty > 1;
            return (
              <Group key={line.id} gap="md" wrap="nowrap" align="flex-start">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm">{items.label(line.inventoryItemId)}</Text>
                  {(line.sku || line.description) && (
                    <Text size="xs" c="dimmed">
                      {/* Their own name for it, kept so the invoice in your hand can be matched to
                          the line — but second, because it names their catalogue, not your stock. */}
                      {[line.description, line.sku].filter(Boolean).join(' · ')}
                    </Text>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Text size="sm" fw={600}>
                    {formatMoney(
                      Math.round(line.priceMinor / Math.max(1, line.packQty || 1)),
                      line.currency || currency,
                    )}
                    {unit ? ` a ${unit}` : ' each'}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {packed
                      ? `${formatMoney(line.priceMinor, line.currency || currency)} for ${formatQty(line.packQty, unit)} · `
                      : ''}
                    priced {formatDate(line.effectiveAt)}
                  </Text>
                  {/* On the row, because "what were they charging in March" is asked ABOUT one
                      thing, standing in front of the price they are quoting today. */}
                  {(pricesOnFile.get(line.inventoryItemId) ?? 0) > 1 && (
                    <Anchor
                      component="button"
                      type="button"
                      size="xs"
                      onClick={() =>
                        setShowingHistoryFor({
                          id: line.inventoryItemId,
                          name: items.label(line.inventoryItemId),
                        })
                      }
                    >
                      {pricesOnFile.get(line.inventoryItemId)} prices on file
                    </Anchor>
                  )}
                </div>
              </Group>
            );
          })}
        </Stack>
      )}

      {adding && (
        <RecordPriceForm
          supplierId={supplierId}
          supplierName={supplierName}
          currency={currency}
          onClose={() => setAdding(false)}
          onSaved={refreshPrices}
        />
      )}

      {importing && (
        <ImportPriceListForm
          supplierId={supplierId}
          supplierName={supplierName}
          currency={currency}
          onClose={() => setImporting(false)}
          onSaved={refreshPrices}
        />
      )}

      {showingHistoryFor && (
        <PriceHistoryModal
          supplierId={supplierId}
          supplierName={supplierName}
          inventoryItemId={showingHistoryFor.id}
          itemName={showingHistoryFor.name}
          onClose={() => setShowingHistoryFor(null)}
        />
      )}
    </>
  );
}

/**
 * Writing down what they quoted.
 *
 * Asked the way a price is actually quoted on this island — so much for a case of so many — and
 * stored per unit alongside it, because everything downstream counts in units.
 */
function RecordPriceForm({
  supplierId,
  supplierName,
  currency,
  onClose,
  onSaved,
}: {
  supplierId: string;
  supplierName: string;
  currency: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const stock = useStockItems();
  const [inventoryItemId, setInventoryItemId] = useState('');
  const [packQty, setPackQty] = useState<NumberInputValue>(1);
  const [price, setPrice] = useState<NumberInputValue>('');
  const [priceCurrency, setPriceCurrency] = useState(currency);
  // The BAR's calendar, not the reader's browser: `useIslandToday` carries the reasoning and the
  // six times a UTC-sliced date has answered tomorrow from six in the evening.
  const islandToday = useIslandToday();
  const [effectiveAt, setEffectiveAt] = useState(islandToday);
  const [error, setError] = useState<string | null>(null);

  const unit = stock.unitOf(inventoryItemId);
  const pack = Math.max(typedNumber(packQty) ?? 1, 1);
  const priceMinor = toMinorUnits(price) ?? 0;

  const save = useMutation({
    mutationFn: () =>
      rawApi('POST', CATALOG_ITEMS_PATH, {
        supplierId,
        inventoryItemId,
        packQty: pack,
        // One counted unit per unit of the pack: the bar's own unit is the only one every other
        // screen speaks, and inventing a second one here is how a case of twelve becomes a case
        // of twelve litres.
        packUnitQty: 1,
        packUnit: unit || 'unit',
        priceMinor,
        currency: priceCurrency,
        effectiveAt,
      }),
    onSuccess: () => {
      onSaved();
      onClose();
      notifyDone(
        'Price recorded',
        `${formatMoney(Math.round(priceMinor / pack), priceCurrency)} a ${unit || 'unit'} from ${supplierName}. Any earlier price for it is kept as history.`,
      );
    },
    onError: (err: Error) => notifyError(err, 'record this price'),
  });

  const submit = () => {
    if (!inventoryItemId) return setError('Say what the price is for');
    if (priceMinor <= 0) return setError('Say what they charge');
    if (!effectiveAt) return setError('Say when this price applies from');
    setError(null);
    save.mutate();
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title={`Record a price from ${supplierName}`}
      size="lg"
      // Half-completed forms are lost to a stray click otherwise.
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        <EntitySelect
          entity="inventoryItem"
          label="What it is a price for"
          required
          value={inventoryItemId || null}
          onChange={(v) => setInventoryItemId(v ?? '')}
          disabled={save.isPending}
        />

        <Group grow align="flex-start">
          <NumberInput
            label={`How many ${unit || 'units'} they sell it in`}
            description="A case of twelve is 12. Put 1 if they sell them singly."
            min={1}
            value={packQty}
            onChange={setPackQty}
            disabled={save.isPending}
          />
          <NumberInput
            label={pack > 1 ? `What the ${pack} cost together` : 'What one costs'}
            required
            min={0}
            prefix={`${currencySymbol(priceCurrency)} `}
            decimalScale={2}
            thousandSeparator=","
            hideControls
            value={price}
            onChange={setPrice}
            disabled={save.isPending}
          />
        </Group>

        <Group grow align="flex-start">
          <Select
            label="They quoted it in"
            required
            data={enumOptions('currency')}
            value={priceCurrency}
            onChange={(v) => setPriceCurrency(v ?? currency)}
            allowDeselect={false}
            comboboxProps={{ withinPortal: true }}
            disabled={save.isPending}
          />
          <DateInput
            label="Priced from"
            description="The day this price started, so an older one stays true for orders placed before it"
            required
            value={effectiveAt || null}
            onChange={(v) => setEffectiveAt(v ?? '')}
            valueFormat="D MMM YYYY"
            popoverProps={{ withinPortal: true }}
            disabled={save.isPending}
          />
        </Group>

        {priceMinor > 0 && (
          <Text size="sm" c="dimmed">
            That works out at <strong>{formatMoney(Math.round(priceMinor / pack), priceCurrency)}</strong>
            {unit ? ` a ${unit}` : ' each'} — the figure an order to them will use.
          </Text>
        )}

        {error && (
          <Text size="sm" c="red">
            {error}
          </Text>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={submit}>
            Record the price
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
